import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { produce } from "immer";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";

import { fetchSagaFactory } from "../../../app/sagas/fetchSagaFactory";
import { createCustomNotificationAction } from "../../../app/actions";
import { defaultLocalizer, localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { handleIfOutdated } from "../../../utilities/workflowUtils";
import { validateActivityWithCommand } from "../../../utilities/activityUtils";
import { extractCause, isRawHttpStatusCause } from "../../../utilities/error";
import { putErrorNotification } from "../../../utilities/notificationUtils";

import { generateArchivalContent } from "../actions";
import { isArchiveModalDocumentData } from "../archiveModalTypes";
import { SEGMENT_TYPE } from "../constants";
import { ArchiveModuleFormDescriptor } from "../descriptors";
import {
	isXakteArchivePreparation,
	xaktePreviewDownloadRequest,
	xaktePreparationStatusRequest,
	xaktePreviewRequest,
	XakteArchivePreparation
} from "../xakteApi";

const POLLING_INTERVAL_MS = 5000;

export function* generateXakteArchiveSaga(): SagaGenerator<void> {
	yield* takeLatest(generateArchivalContent.match, function* (action) {
		const { activityId } = action.payload;
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		if (
			!ArchiveModuleFormDescriptor.isInstance(activity?.descriptor) ||
			activity.descriptor.archiveSystem !== "xakte"
		) {
			return;
		}
		if (yield* call(handleIfOutdated)) {
			return;
		}

		const currentData = yield* select(ActivitySelectors.data(activityId));
		if (isArchiveModalDocumentData(currentData) && currentData.document.root.metadata?.message) {
			yield* put(
				ActivityActions.setData({
					activityId,
					data: produce(currentData, draft => {
						if (draft.document.root.metadata) {
							delete draft.document.root.metadata["status"];
							delete draft.document.root.metadata["message"];
						}
					})
				})
			);
		}
		if (!(yield* call(validateActivityWithCommand, activityId, Commands.validatePart({ focusFirstError: true })))) {
			return;
		}

		const data = yield* select(ActivitySelectors.data(activityId));
		if (!isArchiveModalDocumentData(data)) {
			return;
		}
		yield* put(
			ActivityActions.setData({
				activityId,
				data: produce(data, draft => {
					if (!draft.document.root.metadata) {
						draft.document.root.metadata = {};
					}
					draft.document.root.metadata.busy = true;
					draft.document.root.metadata.cancelDisabled = true;
				})
			})
		);

		yield* call(
			fetchSagaFactory(
				xaktePreviewRequest(
					activity.descriptor.instance,
					collectSelectedAttachmentIds(data.document),
					{
						archiveFile: {
							subject: data.document.root.betreff,
							reference: data.document.root.aktenkennzeichen,
							archiveCase: {
								subject: data.document.root.betreff,
								reference: data.document.root.bezug,
								primaryDocument: {
									subject: data.document.root.betreff,
									reference: data.document.root.bezug,
									documentType: data.document.root.dokumentenArt
								}
							}
						}
					},
					{
						absender: data.document.root.absender ?? null,
						mappenNummer: data.document.root.mappenNummer ?? null,
						papierform: data.document.root.papierform ?? null
					},
					activity.descriptor.archiveLocation
				),
				function* handleResponse(response) {
					const started: unknown = yield* call(response.json.bind(response));
					if (!isXakteArchivePreparation(started)) {
						throw new Error("Response is not a valid xAkte preparation.");
					}
					const result =
						started.status === "PENDING"
							? yield* call(pollXaktePreparation, activityId, started.preparationId)
							: started;
					if (!result || result.status !== "SUCCEEDED" || !result.previewId || !result.fileName) {
						throw new Error(result?.errorMessage ?? "Die xAkte-Vorschau konnte nicht erstellt werden.");
					}

					const archivePreviewUrl = yield* call(downloadXaktePreview, result.previewId);
					const latestData = yield* select(ActivitySelectors.data(activityId));
					if (!isArchiveModalDocumentData(latestData)) {
						URL.revokeObjectURL(archivePreviewUrl);
						return;
					}
					const warningMessage = result.warnings.map(warning => warning.message).join(" ");
					yield* put(
						ActivityActions.setData({
							activityId,
							data: produce(latestData, draft => {
								if (!draft.document.root.metadata) {
									draft.document.root.metadata = {};
								}
								draft.document.root.metadata.jobUuid = result.previewId ?? undefined;
								draft.document.root.metadata.previewFileName = result.fileName ?? undefined;
								draft.document.root.metadata.archivePreviewUrl = archivePreviewUrl;
								draft.document.root.metadata.busy = false;
								draft.document.root.metadata.cancelDisabled = false;
								if (warningMessage) {
									draft.document.root.metadata.status = "warning";
									draft.document.root.metadata.message = warningMessage;
								} else {
									delete draft.document.root.metadata["status"];
									delete draft.document.root.metadata["message"];
								}
							})
						})
					);
					yield* put(ActivityActions.setDirty({ activityId, dirty: true }));
					yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(true) }));
				},
				function* handleError(error: unknown) {
					const cause = extractCause(error);
					const localizableMessage =
						cause && !isRawHttpStatusCause(cause)
							? localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.xakte.generatingErrorWithCause,
									{ cause: { type: "plain", value: cause } }
								)
							: localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.xakte.generatingError
								);
					const latestData = yield* select(ActivitySelectors.data(activityId));
					if (isArchiveModalDocumentData(latestData)) {
						yield* put(
							ActivityActions.setData({
								activityId,
								data: produce(latestData, draft => {
									if (!draft.document.root.metadata) {
										draft.document.root.metadata = {};
									}
									draft.document.root.metadata.status = "error";
									draft.document.root.metadata.message = (
										defaultLocalizer(localizableMessage) ?? ""
									).trim();
									draft.document.root.metadata.busy = false;
									draft.document.root.metadata.cancelDisabled = false;
								})
							})
						);
					}
					yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(false) }));
					yield* put(
						createCustomNotificationAction({
							title: { key: RESOURCE_KEYS.features.notification.title.error },
							message: localizableMessage,
							severity: "error",
							activityId,
							group: `xakteArchiving:${activityId}`
						})
					);
				}
			)
		);
	});
}

function* downloadXaktePreview(previewId: string): SagaGenerator<string> {
	let archivePreviewUrl: string | undefined;
	yield* call(
		fetchSagaFactory(
			xaktePreviewDownloadRequest(previewId),
			function* handleResponse(response) {
				const blob = yield* call(response.blob.bind(response));
				archivePreviewUrl = URL.createObjectURL(blob);
			},
			function* handleError(error: unknown) {
				yield* call(putErrorNotification, "Das xDomea Archiv konnte nicht heruntergeladen werden", error);
				throw error;
			}
		)
	);
	if (!archivePreviewUrl) {
		throw new Error("Die xAkte-Vorschau konnte nicht heruntergeladen werden.");
	}
	return archivePreviewUrl;
}

export function* pollXaktePreparation(
	activityId: string,
	preparationId: string
): SagaGenerator<XakteArchivePreparation | undefined> {
	let result: XakteArchivePreparation | undefined;
	yield* call(
		fetchSagaFactory(
			xaktePreparationStatusRequest(preparationId),
			function* handleResponse(response) {
				const responseBody: unknown = yield* call(response.json.bind(response));
				if (!isXakteArchivePreparation(responseBody)) {
					throw new Error("Response is not a valid xAkte preparation.");
				}
				result = responseBody;
				if (responseBody.status !== "PENDING") {
					return undefined;
				}
				const activity = yield* select(ActivitySelectors.activityById(activityId));
				if (!activity) {
					result = undefined;
					return undefined;
				}
				return POLLING_INTERVAL_MS;
			},
			function* handleError(error: unknown) {
				yield* put(createCustomNotificationAction({}));
			}
		)
	);
	return result;
}

export function collectSelectedAttachmentIds(document: {
	root: {
		attachments?: Array<{ value: string }> | null;
		initialAttachments?: Array<{ value: string }> | null;
		sortierungAttachments?: Array<{ id: string }> | null;
		sortierungGeneratedDocuments?: Array<{ id: string }> | null;
		sortierungSegments?: Array<{ id: string }> | null;
	};
}): string[] {
	const selectedInitialAttachmentIds = document.root.initialAttachments?.map(item => item.value) ?? [];
	const selectedIds = new Set([
		...(document.root.attachments?.map(item => item.value) ?? []),
		...selectedInitialAttachmentIds
	]);
	const attachmentIdsBySegment: Record<string, string[]> = {
		[SEGMENT_TYPE.INITIALE_ATTACHMENTS]: selectedInitialAttachmentIds,
		[SEGMENT_TYPE.HINZUGEFUEGTE_ATTACHMENTS]:
			document.root.sortierungAttachments?.map(item => item.id).filter(id => selectedIds.has(id)) ?? [],
		[SEGMENT_TYPE.GENERIERTE_DOKUMENTE]:
			document.root.sortierungGeneratedDocuments?.map(item => item.id).filter(id => selectedIds.has(id)) ?? []
	};
	const configuredSegments = document.root.sortierungSegments?.map(item => item.id) ?? [
		SEGMENT_TYPE.INITIALE_ATTACHMENTS,
		SEGMENT_TYPE.HINZUGEFUEGTE_ATTACHMENTS,
		SEGMENT_TYPE.GENERIERTE_DOKUMENTE
	];
	const orderedIds = configuredSegments.flatMap(segment => attachmentIdsBySegment[segment] ?? []);
	for (const selectedId of selectedIds) {
		if (!orderedIds.includes(selectedId)) {
			orderedIds.push(selectedId);
		}
	}
	return [...new Set(orderedIds)];
}
